import { useLocalizedHref } from "@libraries/web-framework";
import { useIntl } from "react-intl";

import { BannerProps } from "@/components/wpl/banner-alert/src";
import { ImageProps } from "@/components/wpl/image/src";
import { ProductNavBarProps } from "@/components/wpl/product-navbar/src";
import { LogoBrand, ProductKeyTypes } from "@/libraries/wpl/foundations/src";
import { PaidHeroProps } from "@/patterns/paid-hero";
import { PaidSocialProofProps } from "@/patterns/paid-landings/paid-social-proof";
import { PaidTabsProps } from "@/patterns/paid-landings/paid-tabs";
import { PatternThreeAlternatingImageTextProps } from "@/patterns/three-alternating-image-text/src/three-alternating-image-text";
import { PatternCTAMobileProps } from "@/patterns/wpl/cta-primary/src";

import PaidLandingTemplate from ".";

interface HeroProps extends PaidHeroProps {
    styles: object;
}
interface CtaProps extends PatternCTAMobileProps {
    styles: object;
}
interface TabsProps extends PaidTabsProps {
    styles: object;
}

interface PaidLandingJsmProps {
    productNavProps?: ProductNavBarProps;
    bannerAlertProps?: BannerProps;
    heroProps: HeroProps;
    tabsProps: TabsProps;
    alternatingTextAndImageProps: PatternThreeAlternatingImageTextProps;
    ctaProps: CtaProps;
    testimonialProps?: PaidSocialProofProps;
}

export const PaidLandingJsmTemplate = (props: PaidLandingJsmProps) => {
    const intl = useIntl();

    const finalProps = {
        productNavProps: {
            productHref: useLocalizedHref("/software/jira/service-management"),
            productName: "jiraservicemanagement" as LogoBrand,
            ariaLabel: intl.formatMessage({
                id: "wacJiraServiceManagementNavigation.logo-label",
                defaultMessage: "Jira Service Management logo"
            }),
            dataProductKey: "jiraServiceManagement" as ProductKeyTypes,
            showFreeHrefButton: false,
            logoLinkWrapperWidth: "279px"
        },
        bannerAlertProps: {
            identifier:
                "jsm-software-paid-lp-jsm-default.banner-alert.airtrack",
            text: intl.formatMessage({
                id: "JsmSoftwarePagePaidLpJsmDefault.bannerAlert.text",
                defaultMessage: "Atlassian has acquired AirTrack!"
            }),
            linkText: intl.formatMessage({
                id: "JsmSoftwarePagePaidLpJsmDefault.bannerAlert.linkText",
                defaultMessage: "Learn more"
            }),
            linkUrl: useLocalizedHref(
                "/blog/announcements/atlassian-acquires-airtrack"
            )
        },
        testimonialProps: {
            headerText: intl.formatMessage({
                id: "JsmSoftwarePagePaidLpJsmDefault.socialProof.heading",
                defaultMessage:
                    "Teams across the world rely on Jira Service Management"
            }),
            logos: [
                {
                    src: "https://wac-cdn.atlassian.com/dam/jcr:5da8c33f-c3f7-4c40-93da-1194835f0cb5/square-n50.svg?cdnVersion=ecc",
                    alt: intl.formatMessage({
                        id: "JsmSoftwarePagePaidLpJsmDefault.socialProof.squareAlt",
                        defaultMessage: "Square logo"
                    }),
                    loading: "lazy"
                },
                {
                    src: "https://wac-cdn.atlassian.com/dam/jcr:e9ed616c-1d41-44c1-8186-de5cc0497ef1/sothebys-n50.svg?cdnVersion=ecc",
                    alt: intl.formatMessage({
                        id: "JsmSoftwarePagePaidLpJsmDefault.socialProof.sothebysAlt",
                        defaultMessage: "Sotheby's logo"
                    }),
                    loading: "lazy"
                },
                {
                    src: "https://wac-cdn.atlassian.com/dam/jcr:55429e74-ce5d-4feb-83de-9e937c45486f/dominos-n50.svg?cdnVersion=ecc",
                    alt: intl.formatMessage({
                        id: "JsmSoftwarePagePaidLpJsmDefault.socialProof.dominosAlt",
                        defaultMessage: "Domino's logo"
                    }),
                    loading: "lazy"
                },
                {
                    src: "https://wac-cdn.atlassian.com/dam/jcr:f90244be-7957-4559-ae89-22d0648599d1/puppet-n50.svg?cdnVersion=ecc",
                    alt: intl.formatMessage({
                        id: "JsmSoftwarePagePaidLpJsmDefault.socialProof.puppetAlt",
                        defaultMessage: "Puppet logo"
                    }),
                    loading: "lazy"
                },
                {
                    src: "https://wac-cdn.atlassian.com/dam/jcr:27bfd8db-8705-4f7f-a642-3f616eeefbe0/twitter-n50.svg?cdnVersion=ecc",
                    alt: intl.formatMessage({
                        id: "JsmSoftwarePagePaidLpJsmDefault.socialProof.twitterAlt",
                        defaultMessage: "Twitter logo"
                    }),
                    loading: "lazy"
                },
                {
                    src: "https://wac-cdn.atlassian.com/dam/jcr:cb854751-48f0-421e-aa63-f3528286e9dc/telegraph-n50.svg?cdnVersion=ecc",
                    alt: intl.formatMessage({
                        id: "JsmSoftwarePagePaidLpJsmDefault.socialProof.telegraphAlt",
                        defaultMessage: "The Telegraph logo"
                    }),
                    loading: "lazy"
                }
            ] as ImageProps[]
        },
        ...props
    };

    return <PaidLandingTemplate {...finalProps} />;
};
